<template>
  <el-dialog
    :title="isEdit ? '编辑Banner' : '添加Banner'"
    v-model="dialogVisible"
    width="600px"
  >
    <el-form :model="form" :rules="rules" ref="formRef" label-width="100px">
      <el-form-item label="标题" prop="title">
        <el-input v-model="form.title" placeholder="请输入标题" />
      </el-form-item>

      <el-form-item label="图片" prop="image_url">
        <el-upload
          class="banner-uploader"
          :action="`${backendUrl}/file/upload/preview`"
          :headers="uploadHeaders"
          :show-file-list="false"
          :on-success="handleUploadSuccessV2"
          :on-error="handleUploadError"
          :before-upload="beforeUpload"
          name="file"
        >
          <div class="preview-container">
            <img 
              v-if="form.image_url" 
              :src="form.image_url" 
              class="uploaded-image"
              @load="handleImageLoad"
              ref="previewImage"
            >
            <div v-else class="upload-placeholder">
              <el-icon><Plus /></el-icon>
              <div class="upload-text">点击上传图片</div>
            </div>
          </div>
        </el-upload>
        <div class="image-info" v-if="imageInfo.width">
          <div class="upload-tip">建议尺寸: {{ getRecommendedSize() }}</div>
          <div class="upload-tip">当前尺寸: {{ imageInfo.width }} x {{ imageInfo.height }}</div>
        </div>
      </el-form-item>

      <el-form-item label="链接">
        <el-input v-model="form.link_url" placeholder="请输入链接" />
      </el-form-item>

      <el-form-item label="排序" prop="priority">
        <el-input-number v-model="form.priority" :min="0" />
      </el-form-item>

      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="form.status">
          <el-radio label="active">启用</el-radio>
          <el-radio label="inactive">禁用</el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="loading">
          确定
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import request from '@/api/request';
import { Plus } from '@element-plus/icons-vue'
import type { FormInstance, UploadProps } from 'element-plus'
import { ElMessage } from 'element-plus'

const backendUrl = ref(import.meta.env.VITE_JAVA_API_URL)


const props = defineProps<{
  visible: boolean
  type: 'main' | 'slide' | 'ad' | 'ad_left' | 'ad_right'
  editData?: any
}>()

const emit = defineEmits(['update:visible', 'success'])

const dialogVisible = ref(false)
const loading = ref(false)
const formRef = ref<FormInstance>()



// 添加isEdit计算属性
const isEdit = computed(() => !!props.editData)

const form = ref({
  title: '',
  image_url: '',
  link_url: '',
  type: props.type,
  object_name: '',
  priority: 0,
  status: 'active'
})

const rules = {
  title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
  image_url: [{ required: true, message: '请上传图片', trigger: 'change' }],
  priority: [{ required: true, message: '请输入排序', trigger: 'blur' }],
  status: [{ required: true, message: '请选择状态', trigger: 'change' }]
}

// 根据类型返回建议尺寸
const getRecommendedSize = () => {
  switch (props.type) {
    case 'main':
      return '1920x500'
    case 'slide':
      return '300x400'
    case 'ad':
      return '1920x500'
    case 'ad_left':
      return '800x350' // 左侧广告图建议尺寸
    case 'ad_right':
      return '800x350' // 右侧广告图建议尺寸
    default:
      return '建议使用16:9的图片'
  }
}

// 上传相关
const uploadHeaders = computed(() => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`
}))

const beforeUpload: UploadProps['beforeUpload'] = (file) => {
  const isImage = file.type.startsWith('image/')
  if (!isImage) {
    ElMessage.error('只能上传图片文件!')
    return false
  }
  const isLt5M = file.size / 1024 / 1024 < 5
  if (!isLt5M) {
    ElMessage.error('图片大小不能超过 5MB!')
    return false
  }
  return true
}

const handleUploadSuccess = (response: any) => {
  if (response.url) {
    // 确保图片URL是完整的
    const baseUrl = import.meta.env.VITE_API_URL || ''
    form.value.image_url = baseUrl + response.url
    ElMessage.success('上传成功')
    
    // 调试信息
    console.log('Upload success:', {
      response,
      imageUrl: form.value.image_url
    })
  } else {
    ElMessage.error('上传失败')
  }
}

const handleUploadSuccessV2 = async (response: any) => {
  if (response.code == 0) {

    // 上传成功
    const objectName =  response.data.objectName;
    const res =  await request.post(`${backendUrl.value}/file/getImagePreview`, {
      objectName: objectName,
    })
    if(res.code == 0){
      form.value.object_name = objectName;
      form.value.image_url = res.data.data.url;
    }
  } else {
    ElMessage.error('上传失败')
  }
}

const handleUploadError = (error: Error) => {
  console.error('上传失败:', error)
  ElMessage.error('上传失败，请检查网络连接')
}

// 添加图片信息状态
const imageInfo = ref({
  width: 0,
  height: 0
})

const previewImage = ref<HTMLImageElement | null>(null)

// 处理图片加载完成
const handleImageLoad = () => {
  if (previewImage.value) {
    imageInfo.value = {
      width: previewImage.value.naturalWidth,
      height: previewImage.value.naturalHeight
    }
  }
}

// 监听visible变化
watch(() => props.visible, (val) => {
  dialogVisible.value = val
  if (val && props.editData) {
    form.value = { ...props.editData }
  } else {
    form.value = {
      title: '',
      image_url: '',
      link_url: '',
      type: props.type,
      priority: 0,
      status: 'active'
    }
  }
})

watch(() => dialogVisible.value, (val) => {
  emit('update:visible', val)
})

// 提交表单
const handleSubmit = async () => {
  if (!formRef.value) return
  
  await formRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true
      try {
        const url = props.editData 
          ? `${backendUrl.value}/system/banners/${props.editData.id}`
          : `${backendUrl.value}/system/banners`
        
        const response = await fetch(url, {
          method: props.editData ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(form.value)
        })

        if (response.ok) {
          ElMessage.success(props.editData ? '更新成功' : '添加成功')
          dialogVisible.value = false
          emit('success')
        }
      } catch (error) {
        console.error('保存失败:', error)
        ElMessage.error('保存失败')
      } finally {
        loading.value = false
      }
    }
  })
}
</script>

<style scoped>
.banner-uploader {
  text-align: center;
}

.preview-container {
  width: 100%;
  min-height: 200px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  transition: border-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.preview-container:hover {
  border-color: #409EFF;
}

.upload-placeholder {
  width: 100%;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.upload-text {
  font-size: 14px;
  color: #999;
  margin-top: 8px;
}

.uploaded-image {
  max-width: 100%;
  max-height: 400px;
  object-fit: contain;
}

.image-info {
  margin-top: 10px;
  text-align: left;
}

.upload-tip {
  font-size: 12px;
  color: #999;
  line-height: 1.5;
}
</style>